import { MarketTableType } from "types";
import React from "react";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { ColumnType } from "@shared/common/CardsList/types";
import FlatList from "common/Lists/FlatList";
import useExpandedCardsSorters from "common/CardSorters/useExpandedCardsSorters";
import MarketCard from "features/watchlist/AllMarkets/MarketCard";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { useTheme } from "styled-components";
import { NewLookSortersRow } from "features/portfolio/NewLook/styles.ts";

interface MarketsCardsListProps {
  data: MarketTableType[];
  showExchangesModal: (symbol: string) => void;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
}

const MarketsCardsList = ({
  data,
  showExchangesModal,
  loading,
  refreshing,
  onRefresh,
}: MarketsCardsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.WATCHLIST);
  const { isNewLook } = useTheme();
  // New look: a wider symbol column so pairs aren't cut ("DOGE/US…").
  const columnSpans = isNewLook ? NEW_LOOK_COLUMN_SPANS : COLUMN_SPANS;
  const columns: ColumnType<MarketTableType>[] = [
    {
      title: t("table.columns.symbol"),
      key: "symbol",
      span: columnSpans[0],
      sorter: (a, b) => sortByStringProperty(a, b, "symbol"),
    },
    {
      title: t("table.columns.price"),
      key: "price",
      span: columnSpans[1],
      sorter: (a, b) => sortByNumberProperty(a, b, "price"),
    },
    {
      title: t("table.columns.24h_change"),
      key: "graph_data.change",
      span: columnSpans[2],
      sorter: (a, b) => sortByNumberProperty(a, b, "graph_data.change"),
    },
  ];

  const { sortedData, cardsSorters, toggleExpanded, isExpanded } =
    useExpandedCardsSorters({
      data,
      columns,
    });

  const renderContent = ({ item }: { item: MarketTableType }) => {
    return (
      <MarketCard
        market={item}
        columnSpans={columnSpans}
        showExchangesModal={showExchangesModal}
        toggleExpanded={toggleExpanded}
        isExpanded={isExpanded(item?.key)}
      />
    );
  };

  return (
    <FlatList
      data={sortedData || []}
      gap={isNewLook ? 8 : 12}
      refreshing={refreshing}
      onRefresh={onRefresh}
      loading={loading}
      renderContent={renderContent}
      renderTopContainer={() =>
        isNewLook ? (
          <NewLookSortersRow style={{ marginBottom: 8 }}>
            {cardsSorters}
          </NewLookSortersRow>
        ) : (
          <div style={{ marginBottom: 16 }}>{cardsSorters}</div>
        )
      }
      listStyle={{ paddingBottom: 16 }}
      wrapperStyle={{ padding: 16 }}
    />
  );
};

export default MarketsCardsList;

const COLUMN_SPANS = [9, 6, 8];
// New look: the symbol column fits long pairs (1000PEPE/USDT) untruncated.
const NEW_LOOK_COLUMN_SPANS = [11, 7, 5];
